import { type Routine } from '@littlebird/sdk';
import {
  adaptRoutineToViewModel,
  filterRoutineViewModels,
  type RoutineFrequencyFilter,
  type RoutineStatusFilter,
} from '@littlebird/ui-utils';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { type FilterDropdownOption } from '@/components/ui/filter-dropdown-menu';
import { useProactiveAgentsQuery } from '@/features/settings/proactive-agent.query';

import { type RoutineFilter } from './routines.state';

const ROUTINE_STATUS_FILTER_OPTIONS: FilterDropdownOption<RoutineFilter>[] = [
  { value: 'active', label: 'Active', Icon: Icons.playAlt },
  { value: 'suggestions', label: 'Suggestions', Icon: Icons.sparkles },
  { value: 'paused', label: 'Paused', Icon: Icons.pause },
  {
    value: 'experimental',
    label: 'Proactive routines',
    Icon: Icons.wandSparkles,
  },
];

const ROUTINE_SCHEDULE_FILTER_OPTIONS: FilterDropdownOption<RoutineFilter>[] = [
  { value: 'daily', label: 'Daily', Icon: Icons.clock },
  { value: 'weekly', label: 'Weekly', Icon: Icons.calendarDays },
  { value: 'monthly', label: 'Monthly', Icon: Icons.calendar },
];

export const ROUTINE_FILTER_OPTION_GROUPS = [
  ROUTINE_STATUS_FILTER_OPTIONS,
  ROUTINE_SCHEDULE_FILTER_OPTIONS,
];

const ROUTINE_STATUS_FILTERS = new Set<RoutineFilter>([
  'active',
  'suggestions',
  'paused',
  'experimental',
]);

const ROUTINE_SCHEDULE_FILTERS = new Set<RoutineFilter>([
  'daily',
  'weekly',
  'monthly',
]);

export const toLibraryFrequency = (filter: RoutineFilter) => {
  if (filter === 'daily') return 'Daily' as const;
  if (filter === 'weekly') return 'Weekly' as const;
  return 'Monthly' as const;
};

export const splitRoutineFilters = (filters: readonly RoutineFilter[]) => ({
  statusFilters: filters.filter((filter) => ROUTINE_STATUS_FILTERS.has(filter)),
  scheduleFilters: filters.filter((filter) =>
    ROUTINE_SCHEDULE_FILTERS.has(filter)
  ),
});

export const useProactiveRoutineChatIds = () => {
  const { data } = useProactiveAgentsQuery();
  return useMemo(
    () => new Set(data?.instances.map((instance) => instance.chat_id)),
    [data]
  );
};

export const filterRoutines = (
  routines: Routine[],
  filters: readonly RoutineFilter[],
  query = '',
  proactiveChatIds: ReadonlySet<string> = new Set()
): Routine[] => {
  const { statusFilters, scheduleFilters } = splitRoutineFilters(filters);
  const matchedIds = new Set(
    filterRoutineViewModels(
      routines.map((routine) => {
        const viewModel = adaptRoutineToViewModel(routine, []);
        const isProactive =
          !!routine.createdInChatId &&
          proactiveChatIds.has(routine.createdInChatId);
        return isProactive ? { ...viewModel, isExperimental: true } : viewModel;
      }),
      {
        query,
        statuses: statusFilters as RoutineStatusFilter[],
        frequencies: scheduleFilters.map(
          toLibraryFrequency
        ) as RoutineFrequencyFilter[],
      }
    ).map((viewModel) => viewModel.id)
  );
  return routines.filter((routine) => matchedIds.has(routine.referenceId));
};
